import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { CompositeFilterDescriptor, getter } from '@progress/kendo-data-query';
import { GridDataType, GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { getData } from 'country-list';
import { getOperationAST } from 'graphql/utilities';
import BulkUpdateDialog from '@components-clean/core/bulk-update-dialog/bulk-update-dialog';
import { ReviewFieldsType } from '@components-clean/core/bulk-update-dialog/field-components/risk-review-field';
import { RiskStatusClosedDateFieldsType } from '@components-clean/core/bulk-update-dialog/field-components/risk-status-closed-date-field';
import {
    IOpenCommentsCountChangedMessage,
    OPEN_COMMENTS_COUNT_CHANGED,
} from '@components-clean/core/comments-side-bar';
import ErrorMessage from '@components-clean/core/error';
import { calculateDefaultFilters } from '@components-clean/core/filter-ui/action-bar/shared/filter.service';
import {
    FILTER_DATA,
    FILTERS,
    IEventBusMessage,
    IFilterEventBusMessage,
} from '@components-clean/core/filter-ui/action-bar/shared/interfaces';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, MultiValueEvaluationCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { ComponentNames, useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { SelectedFieldValuesProps } from '@interfaces-clean/bulk-update.interface';
import AddRiskDialog, {
    RiskFormInputs,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/add-risk-dialog';
import { ILibraryRiskFieldsSubset } from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/assign-risks-to-ctqf-dialog';
import { getAssignableRisks, getFilteredRisks } from '@pages-clean/risk-assessment/helpers';
import { SelectedRowsProps } from '@pages-clean/risk-assessment/interfaces/context';
import { ExtendedCompleteRiskFields } from '@pages-clean/risk-assessment/risk-log/constants';
import AddRisksToStudyDialog from '@pages-clean/risk-assessment/risk-log/dialogs/add-risks-to-study-dialog';
import DeleteRiskDialog from '@pages-clean/risk-assessment/risk-log/dialogs/delete-risk-dialog';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { filterColumns } from '@pages-clean/risk-assessment/risk-log/sections/risks-grid-filter-definition';
import { busDispatch, useEventBus } from '@utils-clean/eventBus';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    CountryInputType,
    NamedItemType,
    RiskStatusType,
    TimeFrequencyType,
} from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import {
    convertReviewFrequency,
    stringDateWithoutTimeFromLocal,
    stringDateWithoutTimeFromUtc,
    stringDateWithoutTimeFromCurrentUtcDate,
    getIsActiveField,
} from '@utils/convertor';
import { ShowError, ShowMessage, ShowSuccess } from '@utils/notification';
import {
    convertRiskScoreColorToWeight,
    mapRiskStatus,
    RiskStatuses,
    mapRiskReviewStatus,
    RiskReviewStatusesForFilter,
} from '@utils/risk-helper';
import { RiskService } from '@utils/risk.service';
import { GET_NAMED_ITEMS_BY_TYPE, GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import { getNamedItemsByType, getNamedItemsByTypeForStudy } from '@queries/generated/common.generated';
import {
    CompleteRiskFields,
    CompleteRiskFields_Risk_evaluationHistory_RiskEvaluation as CompleteRiskFields_evaluationHistory,
    CompleteRiskFields_Risk_observations_RiskObservation as CompleteRiskFields_observations,
} from '@queries/generated/fragments.generated';
// eslint-disable-next-line max-len
import { riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction as RiskActionType } from '@queries/generated/legacy-risk-assessment.generated';
import {
    createRisk,
    createRiskVariables,
    upsertRisk,
    upsertRiskVariables,
    upsertRisks,
    upsertRisksVariables,
    bulkUpdateRisks,
    bulkUpdateRisksVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    ADD_RISK,
    BULK_UPDATE_RISKS,
    CTQ_WIDGET_VALUES,
    GET_CM_CONTROL_CHART_RISKS,
    GET_COMPLETE_RISKS_RISK_LOG,
    GET_LIBRARY_RISKS,
    UPSERT_RISK,
    UPSERT_RISKS,
} from '@queries/risk-assessment';
import { GET_RISK_LIBRARY_CATEGORIES } from '@queries/studies';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';

interface RisksGridProps {
    includeObsolete: boolean;
    includeLtd?: boolean;
    hasInitialFilter?: boolean;
}

const RisksGrid = ({ includeObsolete, includeLtd, hasInitialFilter }: RisksGridProps) => {
    const user = useAuthorization();
    const canView = user?.canRead?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.Read,
    });
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const hasCommentsPermission = user?.can?.({
        module: Constants.Modules.RAM,
        permission: Permissions.Enabled,
        element: Constants.PageElements.RiskLogComments,
    });

    if (!canView) return null;

    const { t, ready } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tCommon } = useTranslation('common');
    const { TooltipCell, ActiveInactiveCell } = useGridCustomCell();
    const { current, setSelectionState, setCurrent } = useContext(RiskLogContext);
    const { study } = current;

    const { confirmDiscardChanges, componentHasChanges } = useUnsavedChangesContext();

    const StudyHasCM2 = study && getIsActiveField('CM2', study.modules);
    const HasCMControlsPermission =
        StudyHasCM2 &&
        user?.can?.({
            module: Constants.Modules.RAM,
            permission: Permissions.Enabled,
            element: Constants.PageElements.CMControls,
        });

    const HasRiskReportsPermission = user?.can?.({
        module: Constants.Modules.RAM,
        permission: Permissions.Enabled,
        element: Constants.PageElements.RiskReports,
    });

    const exportFileName = t('grids.risks.exportFileName');
    const addRiskAction = useRef(false);
    const deletedRiskId = useRef<string | null>(null);
    const scrollToSelectedRisk = useRef(false);
    const suppressGridStateAutoClear = useRef(!!hasInitialFilter);
    const createdOrUpdatedRiskId = useRef<string | null>(null);
    const [riskStatuses, setRiskStatuses] = useState<string[]>([]);
    const [riskReviewStatuses, setRiskReviewStatuses] = useState<string[]>([]);
    const [risks, setRisks] = useState([]);
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });
    const { data: latestOOTValuesForPanel, isLoading } = ControlsHubApi.useGetLatestOOTValuesForPanel(
        study?.id,
        HasCMControlsPermission,
    );
    const categories = filterColumns(HasCMControlsPermission ?? false, includeLtd, hasCommentsPermission);
    const showAddRiskDialog = useBoolean(false);
    const showDeleteRiskDialog = useBoolean(false);
    const showAddRisksToStudyDialog = useBoolean(false);
    const showBulkUpdateDialog = useBoolean(false);
    const showFilterAppliedDialog = useBoolean(false);
    const [selectedRisk, setSelectedRisk] = useState<ExtendedCompleteRiskFields>();
    const {
        value: clearSelectedRows,
        setTrue: setClearSelectedRowsTrue,
        setFalse: setClearSelectedRowsFalse,
    } = useBoolean(false);

    const statuses: { id: string; text: string }[] = Object.assign([], RiskService.riskStatusList);
    const client = useApolloClient();
    const { loading, error, data, refetch } = useQuery(GET_COMPLETE_RISKS_RISK_LOG, {
        variables: { studyId: study ? study.id : null },
    });

    const { data: riskCategoryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CATEGORY },
    });

    // const { data: riskQualityIssueData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
    //     variables: { type: NamedItemType.RISK_QUALITY_ISSUE },
    // });

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } =
        useQuery<getNamedItemsByTypeForStudy>(GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY, {
            variables: {
                studyId: study?.id,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
            },
        });
    const { data: controlStrategyData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CONTROL_STRATEGY },
    });

    const { data: riskVendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.VENDOR },
    });

    const { data: libraryRisks, loading: loadingLibraryRisks } = useQuery(GET_LIBRARY_RISKS, {
        variables: { organisationId: study!.organisation?.id, includeSystemRisks: false },
    });
    const { data: riskLibraryCategories, loading: loadingRiskLibraryCategories } = useQuery(
        GET_RISK_LIBRARY_CATEGORIES,
        {
            variables: { id: study!.id },
        },
    );

    useEventBus<IOpenCommentsCountChangedMessage>(OPEN_COMMENTS_COUNT_CHANGED, ({ parentId, open, all }) => {
        client.cache.modify({
            id: `Risk:${parentId}`,
            fields: {
                commentCounts: () => ({ all, open }),
            },
        });
    });

    const discardCommentSideBarChanges = async () => {
        if (!componentHasChanges(ComponentNames.commentsSideBar)) {
            return true;
        }

        return confirmDiscardChanges();
    };

    const getOOTValue = (associatedPanelRefs: string[] | null) => {
        if (!associatedPanelRefs || associatedPanelRefs.length === 0) return t('shared.notApplicable');
        const panelData = latestOOTValuesForPanel?.filter((panel) => associatedPanelRefs.includes(panel.panelRef!));

        if (panelData?.filter((panel) => panel.active).length === 0) return t('shared.notApplicable');

        if (
            panelData?.some(
                (panel) => panel?.status && (panel?.status.includes('Red') || panel?.status.includes('Amber')),
            )
        )
            return t('shared.yes');
        return t('shared.no');
    };

    const [saveBulkUpdates] = useMutation<bulkUpdateRisks, bulkUpdateRisksVariables>(BULK_UPDATE_RISKS);

    const [saveRiskMutation] = useMutation<createRisk, createRiskVariables>(ADD_RISK);
    const [upsertRiskMutation] = useMutation<upsertRisk, upsertRiskVariables>(UPSERT_RISK);
    const [upsertRisksMutation, { loading: upsertingRisks }] = useMutation<upsertRisks, upsertRisksVariables>(
        UPSERT_RISKS,
    );

    const bulkUpdateAvailableFields = [
        { field: 'category', type: 'categoryLov', required: false, textLimit: 0, ignoreStudyRef: true },
        { field: 'controlStrategy', type: 'controlStrategyLov', required: false, textLimit: 0, ignoreStudyRef: true },
        { field: 'countries', type: 'countriesLov', required: false, textLimit: 0 },
        { field: 'dateIdentified', type: 'date', required: false, textLimit: 0 },
        { field: 'internal', type: 'boolean', required: true, textLimit: 0 },
        { field: 'owner', type: 'ownerLov', required: false, textLimit: 0 },
        // { field: 'riskQualityIssue', type: 'riskQualityIssueLov', required: false, textLimit: 0, ignoreStudyRef: true },
        // { field: 'riskQualityIssueReference', type: 'text', required: false, textLimit: 70 },
        {
            field: 'responsibleFunction',
            type: 'responsibleFunctionLov',
            required: false,
            textLimit: 0,
        },
        { field: 'reviewComplete', type: 'date', required: true, textLimit: 0 },
        { field: 'review', type: 'riskReviewFields', required: true, textLimit: 0 },
        {
            field: 'riskStatusAndClosedDate',
            type: 'riskStatusAndClosedDate',
            required: true,
            textLimit: 0,
            ignoreStudyRef: true,
        },
        { field: 'responsibleVendors', type: 'responsibleVendorLov', required: false, textLimit: 0 },
    ];

    const bulkUpdateRestrictedFields = [
        { field: 'category', type: 'categoryLov', required: false, textLimit: 0, ignoreStudyRef: true },
        { field: 'countries', type: 'countriesLov', required: false, textLimit: 0 },
        { field: 'dateIdentified', type: 'date', required: false, textLimit: 0 },
        { field: 'internal', type: 'boolean', required: true, textLimit: 0 },
        { field: 'reviewComplete', type: 'date', required: true, textLimit: 0 },
        {
            field: 'riskStatusAndClosedDate',
            type: 'riskStatusAndClosedDate',
            required: true,
            textLimit: 0,
            ignoreStudyRef: true,
        },
        { field: 'responsibleVendors', type: 'responsibleVendorLov', required: false, textLimit: 0 },
        // { field: 'riskQualityIssue', type: 'riskQualityIssueLov', required: false, textLimit: 0, ignoreStudyRef: true },
        // { field: 'riskQualityIssueReference', type: 'text', required: false, textLimit: 70 },
    ];

    const getBulkUpdateData = () => {
        const selectedIds = current.selectionState.bulkUpdateRows.map((r) => String(r.id));

        const hasNotApplicable = risks.some(
            (ri: { id: unknown; status: string }) =>
                selectedIds.includes(String(ri.id)) && ri.status === mapRiskStatus(RiskStatusType.NOT_APPLICABLE),
        );

        const hasClosed = risks.some(
            (ri: { id: unknown; status: string }) =>
                selectedIds.includes(String(ri.id)) && ri.status === mapRiskStatus(RiskStatusType.CLOSED),
        );

        const hasObsolete = risks.some(
            (ri: { id: unknown; status: string }) =>
                selectedIds.includes(String(ri.id)) && ri.status === mapRiskStatus(RiskStatusType.OBSOLETE),
        );

        const hasNewDatasetData =
            (HasRiskReportsPermission &&
                HasCMControlsPermission &&
                current.latestDataset &&
                current.riskReviewStartDate &&
                current.latestDataset > current.riskReviewStartDate &&
                risks
                    .filter((risk: { id: unknown; associatedControlsHubPanelIds: string[] | null }) =>
                        selectedIds.includes(String(risk.id)),
                    )
                    .some(
                        (ri: { associatedControlsHubPanelIds: string[] | null }) =>
                            (ri.associatedControlsHubPanelIds?.length ?? 0) > 0,
                    )) ??
            false;

        const fields = hasNotApplicable
            ? bulkUpdateRestrictedFields
            : hasClosed || hasObsolete
              ? bulkUpdateAvailableFields.filter((f) => f.field !== 'review')
              : bulkUpdateAvailableFields;

        const warnings = [
            hasNotApplicable ? t('bulkUpdate.warningMessage2') : undefined,
            hasClosed ? t('bulkUpdate.warningMessageClosedExisting') : undefined,
            hasObsolete ? t('bulkUpdate.warningMessageObsoleteExisting') : undefined,
        ].filter((x): x is string => Boolean(x));

        return warnings.length
            ? { warningMessage2: warnings.join('\n\n'), hasNewDatasetData, fields }
            : { fields, hasNewDatasetData };
    };

    useEffect(() => {
        if (ready) {
            setRiskStatuses(RiskStatuses());
            setRiskReviewStatuses(RiskReviewStatusesForFilter());
        }
    }, [ready]);

    const validLibraryRisks = useMemo(() => {
        if (!riskLibraryCategories.study || loading || loadingLibraryRisks || loadingRiskLibraryCategories) return [];
        return (
            getAssignableRisks({
                data,
                libraryRisks,
                riskLibraryCategories,
            }) ?? []
        );
    }, [data, libraryRisks, riskLibraryCategories, loading, loadingLibraryRisks, loadingRiskLibraryCategories]);

    const evaluationDetails = (evaluationHistory: (CompleteRiskFields_evaluationHistory | null)[] | null) => {
        if (evaluationHistory && evaluationHistory.length > 0) {
            const mostRecentEval = evaluationHistory.reduce((newest, item) => {
                return new Date(item?.updated).getTime() > new Date(newest?.updated).getTime() ? item : newest;
            });
            return {
                impact: mostRecentEval?.impact,
                detectability: mostRecentEval?.detectability,
                likelihood: mostRecentEval?.likelihood,
                rationale: mostRecentEval?.rationale,
                ltd: (mostRecentEval?.likelihood ?? 0) * (mostRecentEval?.detectability ?? 0),
            };
        }
        return {
            ltd: 0,
        };
    };

    const openActionDetails = (observations: (CompleteRiskFields_observations | null)[]) => {
        if (observations && observations.length > 0) {
            const riskActions: RiskActionType[] = [];

            observations
                .filter((obs) => obs && !obs.closedDate && !obs.deleted)
                .forEach((ob: CompleteRiskFields_observations | null) => {
                    if (ob && ob.actions != null) riskActions.push(...(ob.actions as RiskActionType[]));
                });

            return (
                riskActions &&
                riskActions.length &&
                riskActions.filter(
                    (riskAction: RiskActionType) => riskAction && !riskAction.completedDate && !riskAction.deleted,
                ).length
            );
        }
    };

    useEventBus<IFilterEventBusMessage>(FILTERS, (filters) => {
        setFilterState({
            logic: 'and',
            filters,
        });
    });

    useEffect(() => {
        if (
            !loadingResponsibleFunctions &&
            data &&
            riskVendorData &&
            data.risks &&
            (!HasCMControlsPermission || (latestOOTValuesForPanel !== undefined && !isLoading))
        ) {
            const array = data.risks.map((item: CompleteRiskFields) => {
                const mappedStatus = item.status ? mapRiskStatus(item.status) : '';
                return {
                    ...item,
                    createdBy: item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : '',
                    updatedBy: item.updatedBy ? `${item.updatedBy.firstName} ${item.updatedBy.lastName}` : '',
                    convertedOwner: item.owner
                        ? {
                              name: `${item.owner.firstName} ${item.owner.lastName}`,
                              active: item.owner.active,
                          }
                        : {
                              name: '',
                              active: true,
                          },
                    status: mappedStatus,
                    reviewRequired: item.reviewRequired,
                    ...evaluationDetails(item.evaluationHistory),
                    reviewFrequency: convertReviewFrequency(
                        item.reviewFrequencyPeriod ?? 0,
                        TimeFrequencyType[item.reviewFrequencyUnits ?? 'NONE'],
                    ),
                    nextReviewDate: item.nextReviewDate,
                    numActiveCtQs:
                        item.associatedVariables &&
                        item.associatedVariables.length &&
                        item.associatedVariables.filter((ctq) => ctq && ctq.active).length,
                    numOpenObservations:
                        (item.observations &&
                            item.observations.length &&
                            item.observations.filter((obs) => obs && !obs.closedDate && !obs.deleted).length) ??
                        0,
                    numDeletedObservations:
                        (item.observations &&
                            item.observations.length &&
                            item.observations.filter((obs) => obs && obs.deleted).length) ??
                        0,
                    numOpenActions: item.observations ? openActionDetails(item.observations) : 0,
                    countryList: item.countries?.map((a) => a?.name).join(', '),
                    vendorList: item.vendors?.map((a) => a?.name).join(', '),
                    canBeDeleted: item.status === statuses.find((s) => s.id === RiskStatusType.DRAFT)?.id,
                    reviewStatus: item.reviewStatus ? mapRiskReviewStatus(item.reviewStatus) : '',
                    riskRating: t(`riskRating.${item.score?.colour}`),
                    score: {
                        ...item.score,
                        movement:
                            item.score?.movement && item.score?.movement !== 'NO_MOVEMENT'
                                ? t(`grids.risks.movement.${item.score?.movement}`)
                                : '',
                        colour: item.score?.colour !== 'NONE' ? t(`riskRating.${item.score?.colour}`) : null,
                        prevColour: item.score?.prevColour !== 'NONE' ? t(`riskRating.${item.score?.prevColour}`) : '',
                    },
                    created: stringDateWithoutTimeFromUtc(item.created),
                    updated: stringDateWithoutTimeFromUtc(item.updated),
                    numLinkedCMControls: item.associatedControlsHubPanelIds?.length ?? 0,
                    oot: HasCMControlsPermission ? getOOTValue(item.associatedControlsHubPanelIds as string[]) : '',
                };
            });
            // Sort by status
            let sortedResult = array.sort((a: CompleteRiskFields, b: CompleteRiskFields) => {
                if (a.status === b.status) return 0;
                if (
                    a.status !== mapRiskStatus(RiskStatusType.NOT_APPLICABLE) &&
                    a.status !== mapRiskStatus(RiskStatusType.CLOSED) &&
                    (b.status === mapRiskStatus(RiskStatusType.NOT_APPLICABLE) ||
                        b.status === mapRiskStatus(RiskStatusType.CLOSED))
                )
                    return -1;

                return 0;
            });

            // Sort by Color
            sortedResult = sortedResult.sort((a: CompleteRiskFields, b: CompleteRiskFields) => {
                if (
                    a.score?.colour === b.score?.colour ||
                    ((a.status === mapRiskStatus(RiskStatusType.NOT_APPLICABLE) ||
                        a.status === mapRiskStatus(RiskStatusType.CLOSED)) &&
                        b.status !== mapRiskStatus(RiskStatusType.NOT_APPLICABLE) &&
                        b.status !== mapRiskStatus(RiskStatusType.CLOSED))
                )
                    return 0;
                if (
                    a.score?.colour &&
                    b.score?.colour &&
                    convertRiskScoreColorToWeight(a.score?.colour) > convertRiskScoreColorToWeight(b.score?.colour)
                )
                    return -1;

                if (a.score?.colour && !b.score?.colour) return -1;
                return 1;
            });

            const obsoleteLabel = mapRiskStatus(RiskStatusType.OBSOLETE);
            const visibleRisks = includeObsolete
                ? sortedResult
                : sortedResult.filter((r: { status: string }) => r.status !== obsoleteLabel);

            let selectedItem: ExtendedCompleteRiskFields | undefined;

            if (createdOrUpdatedRiskId.current) {
                selectedItem = visibleRisks.find((r: { id: string }) => r.id === createdOrUpdatedRiskId.current) as
                    | ExtendedCompleteRiskFields
                    | undefined;
                createdOrUpdatedRiskId.current = null;
            } else if (current.selectionState.selectedItem) {
                selectedItem = visibleRisks.find(
                    (r: { id: string }) => r.id === current.selectionState.selectedItem?.id,
                ) as ExtendedCompleteRiskFields | undefined;
            }

            if (selectedItem) {
                if (current.selectionState.selectedItemsCount === 1) scrollToSelectedRisk.current = true;

                setSelectionState((old) => ({
                    ...old,
                    selectedItem,
                }));
            } else if (current.selectionState.selectedItem) {
                setSelectionState((old) => ({
                    ...old,
                    selectedItemsCount: 0,
                    bulkUpdateRows: [],
                    selectedItem: undefined,
                }));
            }
            const risksHaveCmControls = visibleRisks.some(
                (ri: ExtendedCompleteRiskFields) => (ri?.numLinkedCMControls ?? 0) > 0,
            );
            if (current.risksHaveCmControlsLinked !== risksHaveCmControls)
                setCurrent((prevState) => ({
                    ...prevState,
                    risksHaveCmControlsLinked: risksHaveCmControls,
                }));

            setRisks(visibleRisks);
            if (addRiskAction.current) {
                const defaultFilters = calculateDefaultFilters(categories);
                setFilterState({
                    logic: 'and',
                    filters: defaultFilters,
                });
            }
            busDispatch<IEventBusMessage>(FILTER_DATA, {
                categories,
                data: visibleRisks,
                clearFilter: addRiskAction.current,
            });
        }
    }, [
        data,
        riskVendorData,
        loadingResponsibleFunctions,
        includeObsolete,
        latestOOTValuesForPanel,
        isLoading,
        HasCMControlsPermission,
    ]);

    const countries = getData().map((countryObject: { code: string; name: string }) => ({
        code: countryObject.code,
        name: countryObject.name,
    }));

    const onSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        setClearSelectedRowsFalse();
        let selectedRowCount = 0;
        let selectedItem: ExtendedCompleteRiskFields;
        let selectedItemsProps: SelectedRowsProps[] = [];
        if (Object.keys(selectedState).length !== 0) {
            const idGetter = getter('id') ?? '';
            const selectedRows = risks.filter((x) => selectedState[idGetter(x)] === true);
            selectedRowCount = selectedRows.length;
            selectedItemsProps = selectedRows.map((item: CompleteRiskFields) => ({
                id: idGetter(item) as number,
            }));
            selectedItem = risks.find(
                (x) => selectedState[idGetter(x)] === true,
            ) as unknown as ExtendedCompleteRiskFields;
        } else {
            selectedItem = event.startDataItem as ExtendedCompleteRiskFields;
        }

        if (selectedItem && selectedItem.id !== current.selectionState.selectedItem?.id)
            scrollToSelectedRisk.current = false;

        setSelectionState((old) => ({
            ...old,
            selectedItemsCount: selectedRowCount,
            bulkUpdateRows: selectedItemsProps,
            selectedItem,
        }));
    };

    const onBulkUpdateClicked = () => {
        if (current.selectionState.bulkUpdateRows.length < 1) return;
        showBulkUpdateDialog.setTrue();
    };

    const onSubmitAddRisk = async (addRiskData: RiskFormInputs) => {
        const riskStatus = RiskStatusType[addRiskData.status?.id as keyof typeof RiskStatusType];

        saveRiskMutation({
            variables: {
                risk: {
                    studyId: study?.id,
                    ownerId: addRiskData.owner?.id,
                    controlStrategyId: addRiskData.controlStrategy?.id,
                    categoryId: addRiskData.category?.id,
                    responsibleFunctionId: addRiskData.responsibleFunction?.id,
                    statement: addRiskData.statement,
                    dateIdentified: addRiskData?.dateIdentified
                        ? stringDateWithoutTimeFromLocal(addRiskData.dateIdentified)
                        : null,
                    closedDate: addRiskData?.closedDate ? stringDateWithoutTimeFromLocal(addRiskData.closedDate) : null,
                    reviewRequired: addRiskData.reviewRequired,
                    reviewFrequencyPeriod: addRiskData.reviewRequired ? addRiskData.reviewFrequencyPeriod : null,
                    reviewFrequencyUnits: addRiskData.reviewRequired
                        ? TimeFrequencyType[addRiskData.reviewFrequencyUnit?.id as keyof typeof TimeFrequencyType]
                        : null,
                    vendorIds: addRiskData.vendors?.map((v) => v.id) ?? [],
                    countries: addRiskData.countries?.map((c) => c as CountryInputType) ?? [],
                    internalOnly: addRiskData.internal,
                    status: riskStatus,
                    // qualityIssueId: addRiskData.qualityIssue?.id,
                    // qualityIssueReference: addRiskData.qualityIssueReference,
                },
            },
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.createRisk) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }

                createdOrUpdatedRiskId.current = response.data.createRisk.id;
                setClearSelectedRowsFalse();
                if (current.selectionState.selectedItemsCount === 0) scrollToSelectedRisk.current = true;

                showAddRiskDialog.setFalse();
                await refetch();
                if (filterState !== undefined)
                    setFilterState({
                        logic: 'and',
                        filters: [],
                    });
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onSubmitEditRisk = async (editRiskData: RiskFormInputs) => {
        const riskStatus = RiskStatusType[editRiskData.status?.id as keyof typeof RiskStatusType];
        const operationName = getOperationAST(GET_CM_CONTROL_CHART_RISKS)?.name?.value;
        upsertRiskMutation({
            variables: {
                risk: {
                    id: addRiskAction.current ? undefined : selectedRisk?.id,
                    studyId: study?.id,
                    libraryReferenceId: selectedRisk?.libraryReferenceId,
                    ownerId: editRiskData.owner?.id,
                    controlStrategyId: editRiskData.controlStrategy?.id,
                    categoryId: editRiskData.category?.id,
                    responsibleFunctionId: editRiskData.responsibleFunction?.id,
                    statement: editRiskData.statement,
                    // The user does not set this in the dialog (they set it in the Complete Review dialog instead)
                    // so the date always comes direct from the DB - treat it as UTC
                    dateLastReviewed: selectedRisk?.dateLastReviewed
                        ? stringDateWithoutTimeFromUtc(selectedRisk.dateLastReviewed)
                        : null,
                    dateIdentified: editRiskData.dateIdentified
                        ? stringDateWithoutTimeFromLocal(editRiskData.dateIdentified)
                        : null,
                    closedDate: editRiskData.closedDate
                        ? stringDateWithoutTimeFromLocal(editRiskData.closedDate)
                        : null,
                    status: riskStatus,
                    recordId: selectedRisk?.recordId,
                    reviewRequired: editRiskData.reviewRequired,
                    reviewFrequencyPeriod: editRiskData.reviewRequired ? editRiskData.reviewFrequencyPeriod : null,
                    reviewFrequencyUnits: editRiskData.reviewRequired
                        ? TimeFrequencyType[editRiskData.reviewFrequencyUnit?.id as keyof typeof TimeFrequencyType]
                        : null,
                    controls: selectedRisk?.controlIds,
                    active: selectedRisk?.active,
                    internalOnly: editRiskData.internal,
                    vendorIds: editRiskData.vendors?.map((v) => v.id) ?? [],
                    countries: editRiskData.countries?.map((c) => c as CountryInputType) ?? [],
                    // qualityIssueId: editRiskData.qualityIssue?.id,
                    // qualityIssueReference: editRiskData.qualityIssueReference,
                },
            },
            refetchQueries: [operationName!],
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.upsertRisk) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }

                const { status, owner, ...rest } = editRiskData;
                const selectedItem = current.selectionState.selectedItem
                    ? ({ ...current.selectionState.selectedItem, ...rest } as ExtendedCompleteRiskFields)
                    : undefined;

                setSelectedRisk((old) =>
                    old
                        ? {
                              ...old,
                              ...rest,
                          }
                        : undefined,
                );
                setSelectionState((old) => ({
                    ...old,
                    selectedItem,
                }));

                const updatedRiskId = response.data.upsertRisk.id ?? selectedRisk?.id ?? null;
                if (updatedRiskId) {
                    createdOrUpdatedRiskId.current = updatedRiskId;
                    scrollToSelectedRisk.current = true;
                }

                showAddRiskDialog.setFalse();
                await refetch();
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const addRisksToStudy = async (selectedLibraryRisks: ILibraryRiskFieldsSubset[], closeDialog: boolean) => {
        upsertRisksMutation({
            variables: {
                risks: selectedLibraryRisks,
            },
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.upsertRisks) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                addRiskAction.current = true;
                if (closeDialog) showAddRisksToStudyDialog.setFalse();
                await refetch();
                setFilterState({
                    logic: 'and',
                    filters: [],
                });
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onCloseAddRisksToStudy = () => {
        showAddRisksToStudyDialog.setFalse();
    };

    const onOpenAddRisksToStudy = () => {
        setClearSelectedRowsTrue();
        setSelectedRisk(undefined);
        setSelectionState(() => ({
            recordId: undefined,
            selectedItem: undefined,
            selectedItemsCount: 0,
            bulkUpdateRows: [],
        }));

        const filteredAssignableRisks = getFilteredRisks(validLibraryRisks);
        if (filteredAssignableRisks.filteredRisks.length > 0) {
            showAddRiskDialog.setFalse();
            showAddRisksToStudyDialog.setTrue();
        } else {
            showAddRisksToStudyDialog.setFalse();
            addRiskAction.current = true;
            showAddRiskDialog.setTrue();
        }
    };

    const onOpenAddRisk = () => {
        showAddRisksToStudyDialog.setFalse();
        addRiskAction.current = true;
        showAddRiskDialog.setTrue();
    };

    const onAddRiskClose = () => {
        showAddRiskDialog.setFalse();
    };

    const onDeleteRiskClose = () => {
        showDeleteRiskDialog.setFalse();
    };

    const onSubmitDeleteRisk = async () => {
        deletedRiskId.current = selectedRisk?.id ?? null;
        await refetch();
        ShowSuccess(tCommon('actionsSuccessfulToast'));
        showDeleteRiskDialog.setFalse();
    };

    const onSubmitBulkUpdate = async (selectedFieldsData: SelectedFieldValuesProps[]) => {
        try {
            const getFieldValue = (field: string, isDateField?: boolean) => {
                const fieldObj = selectedFieldsData.find((f) => f.field === field);
                if (fieldObj) {
                    if (fieldObj.type === 'boolean') {
                        return fieldObj.value === 'True';
                    }
                    if (fieldObj.type === 'responsibleVendors' && fieldObj.value === '') {
                        return [];
                    }
                    if (fieldObj.type === 'date') {
                        if (fieldObj.value === '') return null;
                        return fieldObj.value ? stringDateWithoutTimeFromLocal(fieldObj.value as string) : undefined;
                    }
                    if (
                        (fieldObj.type === 'categoryLov' ||
                            fieldObj.type === 'controlStrategyLov' ||
                            fieldObj.type === 'ownerLov' ||
                            fieldObj.type === 'responsibleFunctionLov' ||
                            fieldObj.type === 'riskQualityIssueLov') &&
                        fieldObj.value === ''
                    ) {
                        /* For the LOV items they can either be null ( not selected in the form).
                         empty, so we need to pass an empty ObjectId so the backend knows to clear and not make it mull */
                        return '000000000000000000000000';
                    }

                    return fieldObj.value;
                }
                return isDateField ? undefined : null;
            };

            const reviewValues = getFieldValue('review') as unknown as ReviewFieldsType;
            const riskStatusAndClosedDateValues = getFieldValue(
                'riskStatusAndClosedDate',
            ) as unknown as RiskStatusClosedDateFieldsType | null;
            scrollToSelectedRisk.current = false;
            const response = await saveBulkUpdates({
                variables: {
                    studyId: study?.id,
                    riskIds: current.selectionState.bulkUpdateRows.map((item) => item.id),
                    controlStrategyId: getFieldValue('controlStrategy') as boolean,
                    status: riskStatusAndClosedDateValues?.riskStatus?.id as RiskStatusType,
                    closedDate: riskStatusAndClosedDateValues?.closedDate
                        ? stringDateWithoutTimeFromLocal(riskStatusAndClosedDateValues.closedDate)
                        : null,
                    categoryId: getFieldValue('category'),
                    countries: getFieldValue('countries') as CountryInputType[],
                    internalOnly: getFieldValue('internal') as boolean,
                    dateIdentified: getFieldValue('dateIdentified', true),
                    ownerId: getFieldValue('owner') as boolean,
                    // qualityIssueId: getFieldValue('riskQualityIssue'),
                    // qualityIssueReference: getFieldValue('riskQualityIssueReference') as string,
                    responsibleFunctionId: getFieldValue('responsibleFunction'),
                    dateLastReviewed: getFieldValue('reviewComplete', true),
                    reviewRequired: reviewValues ? reviewValues.reviewRequired?.ref === 'True' : null,
                    reviewFrequencyPeriod: reviewValues ? reviewValues.reviewFrequencyPeriod : null,
                    reviewFrequencyUnits: reviewValues
                        ? TimeFrequencyType[reviewValues.reviewFrequencyUnit?.id as keyof typeof TimeFrequencyType]
                        : null,
                    vendorIds: getFieldValue('responsibleVendors') as string[],
                },
                refetchQueries: [
                    {
                        query: CTQ_WIDGET_VALUES,
                        variables: { studyId: study?.id },
                    },
                ],
            });

            if (response && response.data && response.data.bulkUpdateRisks) {
                ShowMessage({ text: tCommon('common:actionsSuccessfulToast'), type: 'success' });
                await refetch();
            } else {
                ShowError(tCommon('common:errorMessage'));
            }
        } catch {
            ShowError(tCommon('common:errorMessage'));
        } finally {
            showBulkUpdateDialog.setFalse();
        }
    };

    const columns: GridColumnProps[] = [
        {
            id: 'statement',
            field: 'statement',
            title: t('grids.risks.columns.statement'),
            lockedByDefault: true,
            width: 250,
            customColumn: true,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'riskScore',
            field: 'score',
            title: t('grids.risks.columns.riskScore'),
            customColumn: true,
            width: 250,
            minWidth: 180,
            ragColumn: true,
            evaluationColumn: true,
            filterKey: 'score',
            filter: 'numeric',
            cells: {
                data: (gridCellProps) =>
                    MultiValueEvaluationCell({
                        ...gridCellProps,
                        highlighted: true,
                        showBlankText: false,
                    }),
            },
            multiValueExportTitles: {
                prevScore: t('grids.risks.exportColumns.prevScore'),
                prevColour: t('grids.risks.exportColumns.prevColour'),
                score: t('grids.risks.exportColumns.score'),
                colour: t('grids.risks.exportColumns.colour'),
                movement: t('grids.risks.exportColumns.movement'),
            },
        },
        {
            id: 'riskEvaluation',
            field: 'aa',
            title: t('grids.risks.columns.riskEvaluation'),
            enhancedGroupedColumnToggling: true,
            children: [
                {
                    id: 'likelihood',
                    field: 'likelihood',
                    title: t('grids.risks.columns.likelihood'),
                    width: 80,
                    customColumn: true,
                    filter: 'numeric',
                },
                {
                    id: 'impact',
                    field: 'impact',
                    title: t('grids.risks.columns.impact'),
                    width: 80,
                    customColumn: true,
                    filter: 'numeric',
                },
                {
                    id: 'detectability',
                    field: 'detectability',
                    title: t('grids.risks.columns.detectability'),
                    width: 100,
                    customColumn: true,
                    filter: 'numeric',
                },
                ...(includeLtd
                    ? [
                          {
                              id: 'ltd',
                              field: 'ltd',
                              title: t('grids.risks.columns.ltd'),
                              width: 100,
                              customColumn: true,
                              filter: 'numeric' as GridDataType,
                          },
                      ]
                    : []),
                {
                    id: 'rationale',
                    field: 'rationale',
                    title: t('grids.risks.columns.rationale'),
                    width: 300,
                    customColumn: true,
                    filter: 'text',
                    cells: {
                        data: TooltipCell({
                            isCsv: false,
                        }),
                    },
                },
            ],
        },
        ...(hasCommentsPermission
            ? [
                  {
                      id: 'numOpenComments',
                      field: 'commentCounts.open',
                      title: t('grids.risks.columns.numOpenComments'),
                      width: 130,
                      customColumn: true,
                      filter: 'numeric' as GridDataType,
                  },
              ]
            : []),
        {
            id: 'dateIdentified',
            field: 'dateIdentified',
            title: t('grids.risks.columns.dateIdentified'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'category',
            field: 'category',
            title: t('grids.risks.columns.category'),
            width: 200,
            minWidth: 200,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: riskCategoryData?.namedItemsByType
                ? riskCategoryData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'status',
            field: 'status',
            title: t('grids.risks.columns.riskStatus'),
            customColumn: true,
            minWidth: 150,
            filter: 'text',
            lovData: riskStatuses,
        },
        {
            id: 'closedDate',
            field: 'closedDate',
            title: t('grids.risks.columns.closedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'reviewRequired',
            field: 'reviewRequired',
            title: t('grids.risks.columns.reviewRequired'),
            cells: { data: YesNoCell },
            customColumn: true,
            minWidth: 120,
            filter: 'boolean',
        },
        {
            id: 'reviewFrequency',
            field: 'reviewFrequency',
            title: t('grids.risks.columns.reviewFrequency'),
            customColumn: true,
            width: 100,
            minWidth: 150,
            filter: 'text',
        },
        {
            id: 'dateLastReviewed',
            field: 'dateLastReviewed',
            title: t('grids.risks.columns.lastReviewed'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            id: 'nextReviewDate',
            field: 'nextReviewDate',
            title: t('grids.risks.columns.nextReview'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            id: 'reviewStatus',
            field: 'reviewStatus',
            title: t('grids.risks.columns.reviewStatus'),
            customColumn: true,
            width: 100,
            minWidth: 150,
            filter: 'text',
            lovData: riskReviewStatuses,
        },
        ...(HasCMControlsPermission
            ? [
                  {
                      id: 'numLinkedCMControls',
                      field: 'numLinkedCMControls',
                      title: t('grids.risks.columns.numLinkedCMControls'),
                      width: 140,
                      customColumn: true,
                      filter: 'numeric' as GridDataType,
                  },
                  {
                      id: 'oot',
                      field: 'oot',
                      title: t('grids.risks.columns.oot'),
                      width: 120,
                      customColumn: true,
                      filter: 'text' as GridDataType,
                  },
              ]
            : []),
        // Hidden but not removed as will be added back in at some point.
        // {
        //     id: 'qualityIssue',
        //     field: 'qualityIssue',
        //     title: t('grids.risks.columns.qualityIssue'),
        //     width: 150,
        //     customColumn: true,
        //     filter: 'text',
        //     filterKey: 'name',
        //     lovData: riskQualityIssueData?.namedItemsByType
        //         ? riskQualityIssueData.namedItemsByType.map((item) => item?.name as string)
        //         : [],
        //     cells: {
        //         data: ActiveInactiveCell({
        //             inactive: (dataItem: INamedItem) => dataItem?.active === false,
        //             renderValue: (dataItem: INamedItem) => dataItem?.name,
        //         }),
        //     },
        // },
        // {
        //     id: 'qualityIssueReference',
        //     field: 'qualityIssueReference',
        //     title: t('grids.risks.columns.qualityIssueReference'),
        //     width: 200,
        //     filter: 'text',
        //     cells: {
        //         data: TooltipCell({
        //             isCsv: false,
        //         }),
        //     },
        // },
        {
            id: 'owner',
            field: 'convertedOwner',
            title: t('grids.risks.columns.owner'),
            customColumn: true,
            width: 1,
            minWidth: 150,
            filterKey: 'name',
            filter: 'text',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'responsibleFunction',
            field: 'responsibleFunction',
            title: t('grids.risks.columns.responsibleFunction'),
            width: 180,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: responsibleFunctionsData?.namedItemsByTypeForStudy
                ? responsibleFunctionsData.namedItemsByTypeForStudy
                      .map((item) => item?.name as string)
                      .sort((a, b) => {
                          return a.localeCompare(b);
                      })
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'controlStrategy',
            field: 'controlStrategy',
            title: t('grids.risks.columns.controlStrategy'),
            width: 180,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: controlStrategyData?.namedItemsByType
                ? controlStrategyData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'internal',
            field: 'internalOnly',
            title: t('grids.risks.columns.internal'),
            cells: { data: YesNoCell },
            customColumn: true,
            minWidth: 100,
            filter: 'boolean',
        },
        {
            id: 'vendors',
            field: 'vendorList',
            title: t('grids.risks.columns.vendors'),
            customColumn: true,
            minWidth: 200,
            filter: 'text',
            lovData: riskVendorData?.namedItemsByType
                ? riskVendorData.namedItemsByType.map((item) => item?.name as string)
                : [],
            lovFilterContains: true,
            cells: {
                data: TooltipCell({
                    isCsv: true,
                    lovArrayField: 'vendors',
                }),
            },
        },
        {
            id: 'countries',
            field: 'countryList',
            title: t('grids.risks.columns.countries'),
            customColumn: true,
            minWidth: 200,
            filter: 'text',
            lovData: countries.map((c) => c.name),
            lovFilterContains: true,
            cells: {
                data: TooltipCell({
                    isCsv: true,
                    countryArrayField: 'countries',
                }),
            },
        },
        {
            id: 'numActiveControls',
            field: 'activeControlCount',
            title: t('grids.risks.columns.numActiveControls'),
            width: 120,
            customColumn: true,
            filter: 'numeric',
        },
        {
            id: 'numActiveCtQs',
            field: 'numActiveCtQs',
            title: t('grids.risks.columns.numActiveCtQs'),
            width: 110,
            customColumn: true,
            filter: 'numeric',
        },
        {
            id: 'numOpenObservations',
            field: 'numOpenObservations',
            title: t('grids.risks.columns.numOpenObservations'),
            width: 140,
            customColumn: true,
            filter: 'numeric',
        },
        {
            id: 'numOpenActions',
            field: 'numOpenActions',
            title: t('grids.risks.columns.numOpenActions'),
            width: 120,
            customColumn: true,
            filter: 'numeric',
        },
        {
            customColumn: true,
            minWidth: 100,
            id: 'id',
            field: 'recordId',
            title: 'ID',
        },
        {
            field: 'createdBy',
            title: t('grids.risks.columns.createdBy'),
            customColumn: true,
            minWidth: 150,
            showByDefault: false,
            alwaysExportColumn: true,
        },
        {
            id: 'created',
            field: 'created',
            title: t('grids.risks.columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
            alwaysExportColumn: true,
        },
        {
            id: 'updatedBy',
            field: 'updatedBy',
            title: t('grids.risks.columns.updatedBy'),
            customColumn: true,
            minWidth: 150,
            showByDefault: false,
            alwaysExportColumn: true,
        },
        {
            id: 'updated',
            field: 'updated',
            title: t('grids.risks.columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
            alwaysExportColumn: true,
        },
    ];

    if (error) return <ErrorMessage />;

    if (!data || loadingResponsibleFunctions) return <Loading />;

    return (
        <>
            <Grid
                excludeHiddenRowMenuActions
                title={t('grids.risks.title')}
                maxHeight="calc(100% - 5px)"
                testDataId="fdr-grid"
                data={risks}
                columns={columns}
                loading={loading}
                useLoadingOverlay
                scrollable="virtual"
                showAddIcon
                addRecordTooltip={t('grids.risks.menuItems.addRisks')}
                dataItemKey="id"
                canEdit={canEdit}
                addCallback={onOpenAddRisksToStudy}
                selectable={{ enabled: true, mode: 'multiple', drag: true }}
                onSelectionChange={onSelectionChange}
                preSelectionChange={discardCommentSideBarChanges}
                showBulkUpdateIcon
                defaultSelectedItem={
                    scrollToSelectedRisk.current ? (current.selectionState.selectedItem ?? undefined) : undefined
                }
                onBulkUpdateClicked={onBulkUpdateClicked}
                filter={filterState || undefined}
                filterListener={(newFilterState) => {
                    setFilterState(newFilterState);
                    if (newFilterState?.filters) {
                        setSelectionState((old) => ({
                            ...old,
                            selectedItemsCount: 0,
                            bulkUpdateRows: [],
                        }));
                    }
                }}
                clearFilter={() => {
                    if (suppressGridStateAutoClear.current) {
                        suppressGridStateAutoClear.current = false;
                        return Promise.resolve(false);
                    }
                    scrollToSelectedRisk.current = false;
                    setFilterState(undefined);
                    return Promise.resolve();
                }}
                clearGridSelectedRows={clearSelectedRows}
                onFilterChange={async (event) => {
                    setFilterState(event.filter);
                }}
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('grids.risks.actions.edit'),
                        onClick: (dataItem) => {
                            const risk = dataItem as ExtendedCompleteRiskFields;
                            setSelectedRisk(risk);
                            addRiskAction.current = false;
                            setClearSelectedRowsFalse();
                            showAddRiskDialog.setTrue();
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'],
                    },
                    {
                        id: 2,
                        label: t('grids.risks.actions.delete'),
                        onClick: (dataItem) => {
                            const risk = dataItem as ExtendedCompleteRiskFields;
                            setClearSelectedRowsFalse();
                            setSelectedRisk(risk);
                            showDeleteRiskDialog.setTrue();
                        },
                        showMenuItem: (dataItem) => {
                            const risk = dataItem as ExtendedCompleteRiskFields;
                            return !risk.deleted;
                        },
                        icon: ['fas', 'trash-xmark'],
                        colorProp: 'Red',
                        disabled: !canEdit,
                    },
                ]}
                exportFilename={`${exportFileName}_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
                    'DD-MMM-YYYY',
                )}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                filterable={false}
                persistentStorageKey={String(PersistentStorageKeys['risk-log-risk-grid'])}
            />
            {showAddRisksToStudyDialog.value && !showAddRiskDialog.value && study && (
                <AddRisksToStudyDialog
                    study={study}
                    onSubmit={addRisksToStudy}
                    onClose={onCloseAddRisksToStudy}
                    onOpenAddRisk={onOpenAddRisk}
                    upsertingRisks={upsertingRisks}
                    validLibraryRisks={validLibraryRisks}
                />
            )}
            {showAddRiskDialog.value && !showAddRisksToStudyDialog.value && study && (
                <AddRiskDialog
                    onClose={onAddRiskClose}
                    onSubmit={addRiskAction.current ? onSubmitAddRisk : onSubmitEditRisk}
                    studyRef={study?.id}
                    risk={addRiskAction.current ? undefined : selectedRisk}
                />
            )}
            {showDeleteRiskDialog.value && selectedRisk && (
                <DeleteRiskDialog onClose={onDeleteRiskClose} onSubmit={onSubmitDeleteRisk} risk={selectedRisk} />
            )}
            {showBulkUpdateDialog.value && !showFilterAppliedDialog.value && (
                <BulkUpdateDialog
                    studyRef={study?.id}
                    onClose={() => {
                        showBulkUpdateDialog.setFalse();
                    }}
                    onSubmit={onSubmitBulkUpdate}
                    dialogTitle={t('bulkUpdate.title')}
                    warningMessage={t('bulkUpdate.warningMessage', {
                        count: current.selectionState.bulkUpdateRows.length,
                    })}
                    {...getBulkUpdateData()}
                    unsavedChangesArea="general"
                />
            )}
        </>
    );
};

export default RisksGrid;
